"use client";

/**
 * `MultiSelectField` — combobox-style multi-pick from a fixed option
 * set. Layout: a row of selected-value chips above a trigger button;
 * clicking the trigger opens a Popover containing a `cmdk`-driven
 * search-and-filter list of every available option. Picking an option
 * toggles it on/off; clicking the chip's `x` removes it.
 *
 * Why Popover + Command (cmdk) instead of a native `<select multiple>`:
 * the native control is functional but visually inconsistent across
 * platforms and lacks search-as-you-type for long option lists. The
 * gotchas log notes that jsdom 25 may stub Popover internals — we
 * verified via `menus.test.tsx` that Popover + Command both work in our
 * test harness today, so the richer UX is achievable without falling
 * back to the native control.
 *
 * State plumbing: rhf's `Controller` exposes the canonical
 * `string[]` form-state value. Toggle logic lives in the field — given
 * a clicked option `value`, we either push it onto the array or filter
 * it out. The chips read from `rhf.value`; the option list inside the
 * popover marks each `CommandItem` as selected via a small ✓ glyph
 * when `value` is in `rhf.value`.
 *
 * Accessibility: the trigger has an aria-label that announces the
 * current selection count. Each chip's remove button has an aria-label
 * naming the chip ("Remove Red"). The popover content has its own
 * focus-trap via Radix's Popover, and `cmdk` handles up/down keyboard
 * navigation inside the list.
 *
 * Empty state: when no options are selected, the trigger shows the
 * field's `placeholder` (or "Select…" as a sensible default). When
 * options are selected, the trigger shows just the chevron — the chips
 * carry the visual weight.
 */

import * as React from "react";
import { Controller } from "react-hook-form";
import { Check, ChevronDown, X } from "lucide-react";
import {
  Button,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { MultiSelectFieldDef } from "../types";
import { FieldShell } from "./field-shell";
import { readErrorMessage } from "./read-error";
import { resolveFieldPath } from "./path";

export interface MultiSelectFieldProps {
  field: MultiSelectFieldDef;
  /** Path prefix from a parent {@link RepeaterField}. See `text-field.tsx`. */
  pathPrefix?: string;
}

/**
 * Small chip rendered above the trigger for each selected value. The
 * `onRemove` callback is wired to the `x` button. We render this as a
 * styled `<span>` rather than the {@link Badge} primitive because we
 * need an interactive remove button glued to the right edge — the
 * standalone Badge is purely visual.
 */
function SelectedChip({
  label,
  onRemove,
  disabled,
}: {
  label: string;
  onRemove: () => void;
  disabled: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-medium text-fg">
      {label}
      <button
        type="button"
        aria-label={`Remove ${label}`}
        onClick={onRemove}
        disabled={disabled}
        className="rounded-sm p-0.5 text-fg-muted hover:bg-bg hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        <X aria-hidden="true" className="size-3" />
      </button>
    </span>
  );
}

export function MultiSelectField({
  field,
  pathPrefix,
}: MultiSelectFieldProps): React.ReactElement | null {
  const { form } = useFormContext();
  const [open, setOpen] = React.useState(false);

  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const path = resolveFieldPath(field.name, pathPrefix);
  const errorMessage = readErrorMessage(form.formState.errors, path);
  const placeholder = field.placeholder ?? "Select…";
  const isDisabled = field.disabled === true;

  return (
    <FieldShell
      htmlFor={path}
      label={field.label}
      {...(field.description !== undefined ? { description: field.description } : {})}
      {...(errorMessage !== undefined ? { error: errorMessage } : {})}
    >
      <Controller
        name={path}
        control={form.control}
        render={({ field: rhf }) => {
          const selected = Array.isArray(rhf.value)
            ? (rhf.value as string[])
            : [];
          const labelByValue = new Map(
            field.options.map((o) => [o.value, o.label]),
          );

          const toggle = (value: string) => {
            const next = selected.includes(value)
              ? selected.filter((v) => v !== value)
              : [...selected, value];
            rhf.onChange(next);
          };

          const remove = (value: string) => {
            rhf.onChange(selected.filter((v) => v !== value));
          };

          return (
            <div className="space-y-1.5">
              {selected.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {selected.map((value) => (
                    <SelectedChip
                      key={value}
                      label={labelByValue.get(value) ?? value}
                      disabled={isDisabled}
                      onRemove={() => {
                        remove(value);
                      }}
                    />
                  ))}
                </div>
              ) : null}
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    id={path}
                    role="combobox"
                    aria-expanded={open}
                    aria-invalid={errorMessage !== undefined ? true : undefined}
                    aria-label={
                      selected.length > 0
                        ? `${field.label} (${String(selected.length)} selected)`
                        : `Select ${field.label}`
                    }
                    onBlur={rhf.onBlur}
                    ref={rhf.ref}
                    disabled={isDisabled}
                    className={cn(
                      "w-full justify-between font-normal text-fg",
                      selected.length === 0 && "text-fg-muted",
                      // Mirror the Input primitive's invalid-state
                      // styling on this combobox trigger so cross-field
                      // and per-field errors carry the same visual
                      // weight (red border + red focus ring).
                      errorMessage !== undefined &&
                        "border-danger focus-visible:ring-danger",
                    )}
                  >
                    <span className="truncate">
                      {selected.length === 0 ? placeholder : "\u00A0"}
                    </span>
                    <ChevronDown
                      aria-hidden="true"
                      className="ml-2 size-4 shrink-0 opacity-50"
                    />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search…" />
                    <CommandList>
                      <CommandEmpty>No results.</CommandEmpty>
                      <CommandGroup>
                        {field.options.map((opt) => {
                          const isSelected = selected.includes(opt.value);
                          return (
                            <CommandItem
                              key={opt.value}
                              value={opt.label}
                              onSelect={() => {
                                toggle(opt.value);
                              }}
                            >
                              <Check
                                aria-hidden="true"
                                className={cn(
                                  "mr-2 size-4",
                                  isSelected ? "opacity-100" : "opacity-0",
                                )}
                              />
                              {opt.label}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          );
        }}
      />
    </FieldShell>
  );
}
